import { FC, useEffect, useState } from "react";

import { BigNumber } from "../../classes/BigNumber";
import { Period } from "../../classes/Period";
import { iChrLaunch, iChrPeriod, iTime, tDiscountPercent } from "../../interfaces";
import { blockchain, discountPercents } from "../../settings";
import { useStoreSelector } from "../../store/store";
import { getContractTime } from "../../utils/Binance/getContractTime";
import { getTime } from "../../utils/Chromia/getTime";
import { EndPeriod, CreateItem, NewPeriod, setHedgetTime, setPickaxeTime, setSlurpyswapTime, SetCreator } from "../../utils/Chromia/operations";
import { getAllLaunchesQuery, getAllPeriodsQuery, getDexTime, getEoptTime, getItems, getStateQuery } from "../../utils/Chromia/queries";
import { getHumanDate, getLastPeriod } from "../../utils/helpers";

import DiscountSelector from "../DiscountSelector/DiscountSelector";
import Header from "../Header/Header";
import Button from "../UI/Button/Button";
import Input from "../UI/Input/Input";

import './SettingsPage.scss';


const SettingsPage: FC = () => {

	const { discountPercent, apiData, userAddress, appContracts, currentTimes } = useStoreSelector(store => store.app);

	const [hasDeposits, setHasDeposits] = useState<{[k in tDiscountPercent]: null | boolean}>(discountPercents.reduce((accum, val) => ({...accum, [val as tDiscountPercent]: null}), {}) as {[k in tDiscountPercent]: null});
	const [currentPeriods, setCurrentPeriods] = useState<{[k in tDiscountPercent]: null | Period}>(discountPercents.reduce((accum, val) => ({...accum, [val as tDiscountPercent]: null}), {}) as {[k in tDiscountPercent]: null});
	const [currentEndTime, setCurrentEndTime] = useState('');

	const [newPickaxeDate, setNewPickaxeDate] = useState('');	// YYY-MM-DDThh:mm
	const [newHedgetDate, setNewHedgetDate] = useState('');	// YYY-MM-DDThh:mm
	const [newDexDate, setNewDexDate] = useState('');	// YYY-MM-DDThh:mm

	const [strikePrice, setStrikePrice] = useState('');
	const [orderRefPrice, setOrderRefPrice] = useState('');
	const [orderDelta, setOrderDelta] = useState('');
	const [orderPrice, setOrderPrice] = useState('');
	const [orderMinPrice, setOrderMinPrice] = useState('');

	const [dexTime, setDexTime] = useState<iTime | null>(null);
	const [eoptTime, setEoptTime] = useState<iTime | null>(null);

	const [createFtAddress, setCreateFtAddress] = useState('');
	const [createUtAddress, setCreateUtAddress] = useState('');
	const [createPricePoolAddress, setCreatePricePoolAddress] = useState('');
	const [createUtDiscountPercent, setCreateUtDiscountPercent] = useState('');

	// const [isChrInited, setIsChrInited] = useState(true);
	const [chrPeriod, setChrPeriod] = useState<iChrPeriod>();
	const [chrLaunch, setChrLaunch] = useState<iChrLaunch>();

	useEffect(() => {
		if (blockchain === 'chromia'){
			getItems();
			updateDexTime();
			updateEoptTime();
		}

		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		updateNewDate(true);
		updateEndTime();

		if (blockchain === 'binance'){
			if (!apiData[discountPercent]) throw new Error('');
			checkDeposits();
			getPeriod();
		}
		if (blockchain === 'chromia'){
			// getItems();
			getChrParams();
		}
		// eslint-disable-next-line
	}, [discountPercent])

	useEffect(() => {
		blockchain === 'binance' && updateNewDate();
		// eslint-disable-next-line
	}, [currentTimes, newPickaxeDate])

	const updateNewDate = (forceUpdate?: boolean) => {
		if (newPickaxeDate && !forceUpdate) return;
		if (currentTimes[discountPercent]) {
			setNewPickaxeDate(getHumanDate(currentTimes[discountPercent]!.ts, 'YYYY-MM-DDThh:mm'));
		}
	}

	const updateDexTime = async () => {
		const ts = await getDexTime();
		setDexTime({
			ts,
			formatted: getHumanDate(ts, 'YYYY-MM-DD hh:mm')
		})
	}
	const updateEoptTime = async () => {
		const ts = await getEoptTime();
		setEoptTime({
			ts,
			formatted: getHumanDate(ts, 'YYYY-MM-DD hh:mm')
		})
	}

	const getChrParams = async () => {
		// const params = await getItem();
		// setIsChrInited(Boolean(params.acc.toString('hex')));
		const periods = await getAllPeriodsQuery();
		const launches = await getAllLaunchesQuery();
		setChrPeriod(periods[periods.length-1]);
		setChrLaunch(launches[launches.length-1]);
	}

	const updateEndTime = async () => {
		switch (blockchain) {
			case 'binance':
				if (!apiData[discountPercent]) throw new Error('');
				// setCurrentEndTime(new Date(apiData[discountPercent]!.config.sbd.end_time * 1000).toUTCString());
				setCurrentEndTime( getHumanDate( apiData[discountPercent]!.config.sbd.end_time, 'YYYY-MM-DD hh:mm' ) );
				break;

			case 'chromia':
				try {
					const chrState = await getStateQuery();
					setCurrentEndTime( getHumanDate(chrState.end_time, 'YYYY-MM-DD hh:mm'));
				} catch (error) {
					console.error(error)
				}
				break;
		
			default:
				throw new Error();
		}
	}

	const checkDeposits = async () => {
		if (!apiData[discountPercent]) throw new Error('');

		if (hasDeposits[discountPercent] === null){
			const response = await fetch(apiData[discountPercent]!.api + '/info/has_deposits');
			if (!response.ok) throw new Error('');
			const res = await response.json();
			if (typeof res !== 'boolean') throw new Error('');

			setHasDeposits(prevState => Object.assign(
				{},
				prevState,
				{[discountPercent]: res}
			))
		}
	}

	const getPeriod = async () => {
		switch (blockchain) {
			case 'binance':
				if (!apiData[discountPercent]) throw new Error('');
		
				if (currentPeriods[discountPercent] === null){
					const period = await getLastPeriod(discountPercent);
		
					setCurrentPeriods(prevState => Object.assign(
						{},
						prevState,
						{[discountPercent]: period}
					))
				}
				break;

			case 'chromia':

				break;

			default:
				throw new Error();
		}
	}

	const startNewPeriod = async () => {
		switch (blockchain) {
			case 'binance':
				if (!userAddress || !appContracts[discountPercent]) throw new Error('');
				// console.log('strikePrice: ', strikePrice, '\norderRefPrice: ', orderRefPrice, '\norderDelta: ', orderDelta, '\norderPrice: ', orderPrice, '\norderMinPrice: ', orderMinPrice, '\nnew BigNumber(strikePrice): ', new BigNumber(strikePrice));
		
				/* const receipt =  */await appContracts[discountPercent]!.methods.NewPeriod(
					new BigNumber(strikePrice).getDecBN(),
					new BigNumber(orderRefPrice).getDecBN(),
					new BigNumber(orderDelta).getDecBN(),
					new BigNumber(orderPrice).getDecBN(),
					new BigNumber(orderMinPrice).getDecBN(),
				).send({
					from: userAddress,
					gas: 3000000,
					gasPrice: '5000000000'
				});
				break;

			case 'chromia':
				NewPeriod(orderRefPrice, orderDelta, orderPrice, orderMinPrice);
				break;

			default:
				throw new Error('');
		}
	}

	const sendNewPickaxeTime = async () => {
		if (!newPickaxeDate) return;
		const date = new Date( newPickaxeDate + 'Z' );   // Z - for UTC time;
		// console.log('output: ', newDate, '\nDate: ', date, '\nts: ', +date/1000);
		switch (blockchain) {
			case 'binance':
				if (!userAddress || !appContracts[discountPercent]) throw new Error('');

				/* const receipt =  */await appContracts[discountPercent]!.methods.set_time(+date/1000).send({
					from: userAddress,
					gas: 3000000,
					gasPrice: '5000000000'
				});
				getContractTime();				
				break;

			case 'chromia':
				try {
					await setPickaxeTime(+date/1000);
					getTime()
				} catch (error) {
					throw error;
				}
				break;
		
			default:
				break;
		}
	}

	const sendNewHedgetTime = async () => {
		if (!newHedgetDate || blockchain !== 'chromia') return;
		const date = new Date( newHedgetDate + 'Z' );   // Z - for UTC time;
		// console.log('output: ', newDate, '\nDate: ', date, '\nts: ', +date/1000);

		try {
			await setHedgetTime(+date/1000);
			updateEoptTime()
		} catch (error) {
			throw error;
		}
	}

	const sendNewDexTime = async () => {
		if (!newDexDate || blockchain !== 'chromia') return;
		const date = new Date( newDexDate + 'Z' );   // Z - for UTC time;
		// console.log('output: ', newDate, '\nDate: ', date, '\nts: ', +date/1000);

		try {
			await setSlurpyswapTime(+date/1000);
			updateDexTime()
		} catch (error) {
			throw error;
		}
	}

	const endPeriod = async () => {
		switch (blockchain) {
			case 'binance':
				if (!userAddress || !appContracts[discountPercent]) throw new Error('');
		
				/* const receipt =  */await appContracts[discountPercent]!.methods.EndPeriod().send({
					from: userAddress,
					gas: 3000000,
					gasPrice: '5000000000'
				});
				break;

			case 'chromia':
				EndPeriod();
				break;
		
			default:
				throw new Error();
		}
	}

	const approveEopt = async () => {
		if (!userAddress || !appContracts[discountPercent]) throw new Error('');

		/* const receipt =  */await appContracts[discountPercent]!.methods.EOptApproveFt(new BigNumber('100000').valueBN18).send({from: userAddress, gas: 3000000, gasPrice: '5000000000'});
	}

	const createChrItem = async () => {
		if (!createFtAddress || !createUtAddress || !createPricePoolAddress || !createUtDiscountPercent) return;
		try {
			await CreateItem(createFtAddress, createUtAddress, createPricePoolAddress, +createUtDiscountPercent/100 + '');
			getChrParams();
		} catch (error) {
			console.error(error);
		}
	}


	return (
		<>
			<Header/>
			<div className="SettingsPage">
				{!userAddress?
					<>Connect wallet</>
				:
					<>
						<DiscountSelector/>
						<br/>
						<div>
							Pickaxe time: {currentTimes[discountPercent]?.formatted} ({currentTimes[discountPercent]?.ts})
							<br/>
							End time: {currentEndTime}
						</div>
						<br/>
						<div className="SettingsPage-newTime">
							<Input
								value={newPickaxeDate}
								setValue={setNewPickaxeDate}
								type='date-time'
								className="SettingsPage-newTime-margin"
								minTime={currentTimes[discountPercent]?.ts}
							/>

							<Button
								action={sendNewPickaxeTime}
							>
								Set New Pickaxe Time
							</Button>
						</div>
						<br/>
						{blockchain === 'chromia' &&
							<>
								<div>
									Hedget time: {eoptTime?.formatted} ({eoptTime?.ts})
								</div>
								<br/>
								<div className="SettingsPage-newTime">
									<Input
										value={newHedgetDate}
										setValue={setNewHedgetDate}
										type='date-time'
										className="SettingsPage-newTime-margin"
										minTime={currentTimes[discountPercent]?.ts}
									/>

									<Button
										action={sendNewHedgetTime}
									>
										Set New Hedget Time
									</Button>
								</div>
								<br/>
								<div>
									Dex time: {dexTime?.formatted} ({dexTime?.ts})
								</div>
								<br/>
								<div className="SettingsPage-newTime">
									<Input
										value={newDexDate}
										setValue={setNewDexDate}
										type='date-time'
										className="SettingsPage-newTime-margin"
										minTime={currentTimes[discountPercent]?.ts}
									/>
									<Button
										action={sendNewDexTime}
									>
										Set New Dex Time
									</Button>
								</div>
								<br/>
							</>
						}
						<br/><br/>
						{blockchain === 'chromia' &&
							<>
								<div>
									Current period: {chrPeriod?.id || '-'} (finished: {chrPeriod?.state === 2? 'yes' : 'no'})
								</div>
								<div>
									Current launch: {chrLaunch?.id || '-'} (finished: {chrLaunch?.state === 2? 'yes' : 'no'})
								</div>
								<br/>
							</>
						}
						{blockchain === 'binance' &&
							<>
								<div>
									Can start period: {hasDeposits[discountPercent]? 'yes' : 'no'}
								</div>
								<div>
									Current period: {currentPeriods[discountPercent]?.number} (finished: {currentPeriods[discountPercent]?.isFinished? 'yes' : 'no'}, End time: {(currentPeriods[discountPercent]?.endTime.formatted)})
								</div>
								<br/>
							</>
						}
						<div className="SettingsPage-newPeriod">
							{blockchain === 'binance' &&
								<div>
									<div className="SettingsPage-newPeriod-title">
										Strike price
									</div>
									<Input
										value={strikePrice}
										setValue={setStrikePrice}
										type="number"
										placeholder="Strike price"
									/>
								</div>
							}
							<div
								className="SettingsPage-newPeriod-margin"
							>
								<div className="SettingsPage-newPeriod-title">
									Order ref price
								</div>
								<Input
									value={orderRefPrice}
									setValue={setOrderRefPrice}
									type="number"
									placeholder="Order ref price"
								/>
							</div>
							<div
								className="SettingsPage-newPeriod-margin"
							>
								<div className="SettingsPage-newPeriod-title">
									Order delta
								</div>
								<Input
									value={orderDelta}
									setValue={setOrderDelta}
									type="number"
									placeholder="Order delta"
								/>
							</div>
							<div
								className="SettingsPage-newPeriod-margin"
							>
								<div className="SettingsPage-newPeriod-title">
									Order price
								</div>
								<Input
									value={orderPrice}
									setValue={setOrderPrice}
									type="number"
									placeholder="Order price"
								/>
							</div>
							<div
								className="SettingsPage-newPeriod-margin"
							>
								<div className="SettingsPage-newPeriod-title">
									Order min price
								</div>
								<Input
									value={orderMinPrice}
									setValue={setOrderMinPrice}
									type="number"
									placeholder="Order min price"
								/>
							</div>
							<Button
								className="SettingsPage-newPeriod-button"
								// disabled={!hasDeposits[discountPercent] || currentPeriods[discountPercent]? !currentPeriods[discountPercent]?.isFinished : false}
								action={startNewPeriod}
							>
								Start new period
							</Button>
						</div>
						<br/><br/>
						<Button
							// disabled={(currentTimes[discountPercent] && currentPeriods[discountPercent])? currentTimes[discountPercent]!.ts < currentPeriods[discountPercent]!.endTime.ts : true}
							action={endPeriod}
						>
							End period
						</Button>

						{blockchain === 'binance' &&
							<>
								<br/><br/>
								<Button
									action={approveEopt}
								>
									EOpt approve
								</Button>
							</>
						}

						{(blockchain === 'chromia') &&
							<>
								<br/><br/>
								<div className="SettingsPage-chrCreate">
									<Input
										value={createFtAddress}
										setValue={setCreateFtAddress}
										type="text"
										className="SettingsPage-chrCreate-margin"
										placeholder="funding token address"
									/>
									<Input
										value={createUtAddress}
										setValue={setCreateUtAddress}
										type="text"
										className="SettingsPage-chrCreate-margin"
										placeholder="underline token address"
									/>
									<Input
										value={createPricePoolAddress}
										setValue={setCreatePricePoolAddress}
										type="text"
										className="SettingsPage-chrCreate-margin"
										placeholder="price pool address"
									/>
									<Input
										value={createUtDiscountPercent}
										setValue={setCreateUtDiscountPercent}
										type="number"
										className="SettingsPage-chrCreate-margin"
										placeholder="underline token discount percent"
									/>
									<Button
										disabled={!createFtAddress || !createUtAddress || !createPricePoolAddress || !createUtDiscountPercent}
										action={createChrItem}
									>
										Create Item
									</Button>
								</div>
								<br/><br/>
								{/* <Button
									action={SetCreator}
								>
									Set creator
								</Button> */}
							</>
						}
					</>
				}
			</div>
		</>
	)
}

export default SettingsPage;